import * as Comlink from 'comlink';
import * as polling from '../queues/polling';
import Contact from "../entities/contact";
import ENTITIES from './actions/index';
import User from "../entities/user";
import localforage from 'localforage';
import ls from '../lib/localStorage';
import store from './store';
import {EntityType} from "../@types/entity";
import * as idb from '../lib/idbd';
import {VERSION} from '../lib/idbd';
import {getEnvironment} from "../lib/get-environment";
import {precomputeContact, precomputeUser} from './hooks';
import {processQueue} from '../queues/processQueue';
import {cast} from "../entities/schemas";
import {RestoreResult} from "../workers/storage.worker";
import Alert from "../lib/alert";
import * as Actions from "./actions/miscActions";

/**
 * Should we do excessive logging?
 */
let logging = ls.getBoolean(ls.RESTORE_LOGGING, false);

interface RestoreState {
    authenticatedFirmId: string;
    authenticatedUserId: string;

    [entity: string]: any;
}

/**
 * Config the storage
 *
 * @param {uuid} firmId
 * @param {uuid} userId
 */
export function config(firmId: string, userId: string) {
    // Set up localForage
    localforage.config({
        name: `Backups_${firmId}_${userId}`,
        driver: localforage.INDEXEDDB,
        description: `Redux backups for ${firmId}/${userId}`
    });
}

/**
 * Load the stored entities into redux.
 *
 * @param {object} state - state from misc
 */
export async function load(state: RestoreState) {
    if (!state.authenticatedFirmId || !state.authenticatedUserId) {
        console.warn('⮂ restore: load called without firm and user Ids', state);
        return;
    }

    _loadDefaults();

    try {
        await _loadBackups(state);
        if (logging) console.debug('%c⮂ load: ✔ Backups loaded', 'color: green');
    }
    catch (err: any) {
        console.error(`%c⮂ load: ✔ Backups not loaded. Error: ${err.message}`, 'color: maroon', err);
    }
    finally {
        // Safety net: if _loadBackups already dispatched RESTORED, don't re-dispatch
        const { restoreStatus } = store.getState().misc;
        if (restoreStatus !== Actions.RESTORED) {
            store.dispatch(Actions.updateLoading(false));
            store.dispatch(Actions.setRestoreStatus(Actions.RESTORED));
        }
    }

    // Clear out pre-computed contacts and users
    // TODO: We do this as needed now, is this required or even wanted?
    // store.getState().contacts.forEach((c: Contact) => precomputeContact(c));
    store.getState().users.forEach((u: User) => precomputeUser(u));
}

function _loadDefaults() {
    let logLevel = '';
    switch (getEnvironment()) {
        case 'P':
            logLevel = 'info';
            break;
        case 'DM':
        case 'T':
        case 'B':
            logLevel = 'log';
            break;
        default:
            logLevel = 'debug';
    }

    ls.setString(ls.LOGGING_LOG_LEVEL, logLevel); // Logging level, anything below this threshold is thrown away
}

async function upgradeBackups(firmId: string, userId: string) {
    // Open the current instance for backups
    let backups = localforage.createInstance({
        name: `Backups_${firmId}_${userId}`,
        driver: localforage.INDEXEDDB,
        storeName: `Backups_${firmId}_${userId}`,
        description: `Redux backups for ${firmId}/${userId}`
    });

    // Check to see if they are old-style
    const backupFirms = await backups.getItem('firms');
    if (!backupFirms) return;

    const backupFirm = backupFirms[0];

    // If so, process them.
    if (backupFirm.hasOwnProperty('_addedOn')) {
        if (logging) console.debug('%c⮂ restore: converting backups from version < 35', "background: orange; color: white");

        // Save all the data locally, then delete, then recreate
        let keys = await backups.keys();
        let data = new Map<string, EntityType[]>();

        await Promise.allSettled(keys.map(async (key) => {
            const single = ENTITIES.find(e => e.plural === key)?.name;
            const oldData: EntityType[] = await backups.getItem(key);
            if (logging) console.debug(`⮂ restore: backups converting ${oldData.length} ${key} values`);

            // Convert all the values to the new format
            const newData = oldData.map(entity => cast<EntityType>(single, entity));

            // Add to local cache
            data.set(key, newData);
        }));

        // Delete the old store
        if (logging) console.debug('%c⮂ restore: dropping backups from version < 35', "background: orange; color: white");
        await backups.dropInstance();

        if (logging) console.debug('%c⮂ restore: recreating backups from version < 35', "background: orange; color: white");
        backups = localforage.createInstance({
            name: `Backups_${firmId}_${userId}`,
            driver: localforage.INDEXEDDB,
            storeName: `Backups_${firmId}_${userId}`,
            description: `Redux backups for ${firmId}/${userId}`
        });

        for (let key of data.keys()) {
            await backups.setItem(key, data.get(key));
        }

        if (logging) console.debug('%c⮂ restore: finished converting backups from version < 35', "background: orange; color: white");
    }
}

async function upgradeAutosave(firmId, userId: string) {
    let autosave = localforage.createInstance({
        name: `Autosave_${firmId}_${userId}`,
        driver: localforage.INDEXEDDB,
        storeName: `Autosave_${firmId}_${userId}`,
        description: `Autosave entries for ${firmId}/${userId}`
    });

    // See if there are entries in any of the autosave stores
    const keys = await autosave.keys();
    const entries: Map<string, EntityType> = new Map();

    for (let key of keys) {
        const entity: EntityType = await autosave.getItem(key);
        if (Object.hasOwn(entity, '_addedOn')) entries.set(key, entity);
    }

    if (entries.size > 0 && VERSION < 35) {
        if (logging) console.debug(`%c⮂ restore: converting autosave entries from version (${VERSION}) < 35`, "background: orange; color: white");

        // Save all the data locally, then delete, then recreate
        await Promise.allSettled(keys.map(async (key) => {
            const single = ENTITIES.find(e => e.plural === key)?.name;
            const oldData: EntityType[] = await autosave.getItem(key);
            if (logging) console.info('old data', oldData);
            if (logging) console.info(`⮂ restore: autosave converting ${oldData.length || 1} ${key} values`);

            // Convert all the values to the new format
            const newData = oldData.map(entity => cast(single, entity));
            await autosave.setItem(key, newData);
            if (logging) console.info(`⮂ restore: converted autosave for ${key}`);
        }));

        // Recreate with new keys
        autosave = localforage.createInstance({
            name: `Autosave_${firmId}_${userId}`,
            driver: localforage.INDEXEDDB,
            storeName: `Autosave_${firmId}_${userId}`,
            description: `Autosave entries for ${firmId}/${userId}`
        });

        for (let [key, entity] of entries) {
            await autosave.setItem(key, cast(key.split(' ')[0], entity));
        }
    }
}

async function upgradeQueues(firmId: string, userId: string) {
    if (logging) console.debug(`%c⮂ restore: converting queues from version (${VERSION}) < 35`, "background: orange; color: white");

    let queuesResult = idb.getOldDb();
    // If not found, nothing to do here.
    if (queuesResult.isNone()) return;

    let openResult = idb.open();
    if (openResult.isErr()) {
        // If an error, we couldn't open the old database. This should not happen, but if it does, we need to let the user know.
        console.error('Unable to open Queues database', openResult.unwrapErr());
        Alert.addSimpleError(`We were unable to upgrade your uncommitted entries during an upgrade (1).  Please contact Support for assistance. Error was ${openResult.unwrapErr().message}`)
        return;
    }

    const allResult = await idb.getAll();
    if (allResult.isErr()) {
        // Errors here could happen even if there is no data in the queues.  We should let the user know.
        console.error('Unable to getAll on the Queues database', allResult.unwrapErr());
        Alert.addSimpleError(`We were unable to upgrade your uncommitted entries during an upgrade.  Please contact Support for assistance. Error was ${allResult.unwrapErr().message}`)
        idb.close();
        return;
    }

    // Safe to close the old one, and delete it
    idb.close();

    // Unwrap the data and start processing it
    const all = allResult.unwrap();

    // Start by creating a new queue database with the firm and user IDs, keyed by addedOn.
    openResult = idb.open(firmId, userId);
    if (openResult.isErr()) {
        // Should not error here, but if it does, alert the user and stop.
        console.error(openResult.unwrapErr());
        Alert.addSimpleError(`We were unable to upgrade your uncommitted entries during an upgrade (2).  Please contact Support for assistance. Error was ${openResult.unwrapErr().message}`)
        return;
    }

    // Cycle through the stores, adding them to the new Queues_firm_user database
    const keys = Object.keys(all);
    for (let entity of keys) {
        const entities = all[entity];
        try {
            // Now write them back to the store
            const addResult = await idb.add(entity, entities);
            if (addResult.isErr()) console.error(addResult.unwrapErr());
        }
        catch (err) {
            console.error(err);
            Alert.addSimpleError(`We were unable to upgrade your uncommitted entries during an upgrade (3).  Please contact Support for assistance. Error was ${err.message}`)
            idb.close();
            return;
        }
    }

    // NOTE: do NOT idb.close() here. The cached `_db` at this point is the
    // firm-user-named Dexie instance opened above with full schema declared
    // and the IDB database created on disk. If we close, getDB() resets
    // `_db = None` and the next idb.add() (e.g. saveTask saga) creates a
    // FRESH Dexie wrapper with no schema declarations — Dexie 4 throws
    // NoSuchDatabaseError on the first table operation because schema
    // versions weren't declared on the new instance. The bare 'Queues' DB
    // on disk has its own filename so deleteDatabase('Queues') below does
    // not affect the firm-user DB or the cached Dexie wrapper around it.
    indexedDB.deleteDatabase('Queues');
}

async function handleUpgrades(firmId: string, userId: string) {
    await upgradeBackups(firmId, userId);
    await upgradeAutosave(firmId, userId);
    if (VERSION > 35) await upgradeQueues(firmId, userId);
}

/**
 * Restore a single entity from IndexedDB backups into Redux.
 */
function restoreEntity(entity: typeof ENTITIES[number], firmId: string, userId: string): Promise<RestoreResult> {
    function fetchSuccess(entities: EntityType[]) {
        store.dispatch(entity.fetchSuccess(entities, false, 0));
    }

    function fetchFailure(err: Error) {
        store.dispatch(entity.fetchFailure(err));
    }

    return window.storageWorker.restore(
        firmId,
        userId,
        entity.name,
        entity.plural,
        Comlink.proxy(fetchSuccess),
        Comlink.proxy(fetchFailure)
    );
}

/**
 * Log restore errors from settled promises.
 */
function logRestoreErrors(settled: PromiseSettledResult<RestoreResult>[]) {
    for (const pr of settled) {
        if (pr.status === 'rejected') {
            console.error(`%c⮂ restore: Error restoring. Reason: %s`, "background: maroon; color: white;", pr.reason);
        }
        else if (pr.status === 'fulfilled') {
            if (pr.value.error) {
                if (!pr.value.error.startsWith(`No storage found for`))
                    console.error(`⮂ restore: Error restoring ${pr.value.entityName}. Reason: %s`, pr.value.error);
            }
        }
    }
}

async function _loadBackups(state: RestoreState) {
    if (logging) console.debug(`⮂ restore: restoring backups for firm ${state.authenticatedFirmId} user ${state.authenticatedUserId}`);

    const start = Date.now();
    const firmId = state.authenticatedFirmId;
    const userId = state.authenticatedUserId;

    if (logging) console.debug('⮂ restore: started restore from backups');

    // Starting with version 34, we use a different object structure for the backups.  We need to convert the old
    // backups to the new structure, but since that would take a long time, better just to delete the old backups
    // and start fresh.
    //
    // We do need to clean up the autosave entries, though. For that we need to convert all entries to the new structure.
    try {
        await handleUpgrades(firmId, userId);
    }
    catch (err) {
        console.error('⮂ restore: handleUpgrades failed, continuing with restore', err);
    }

    if (logging) console.debug(`⮂ restore: database version ${VERSION}`);

    // Phase 1: Restore firm and user first so getCurrentUser()/getCurrentFirm() resolve immediately,
    // allowing Home.tsx to render the Dashboard instead of showing a blank screen.
    const priorityNames = ['firm', 'user'];
    const priorityEntities = ENTITIES.filter(e => priorityNames.includes(e.name));
    const remainingEntities = ENTITIES.filter(e => !priorityNames.includes(e.name));

    if (logging) console.debug(`⮂ restore: Phase 1 — restoring ${priorityEntities.map(e => e.name).join(', ')}`);

    const prioritySettled = await Promise.allSettled(
        priorityEntities.map(entity => restoreEntity(entity, firmId, userId))
    );
    logRestoreErrors(prioritySettled);

    if (logging) console.debug(`%c⮂ restore: Phase 1 ✔ firm+user restored in ${Date.now() - start}ms`, 'color: green');

    // Phase 2: Restore all remaining entities in parallel.
    if (logging) console.debug(`⮂ restore: Phase 2 — restoring ${remainingEntities.length} remaining entities`);

    const remainingSettled = await Promise.allSettled(
        remainingEntities.map(entity => restoreEntity(entity, firmId, userId))
    );
    logRestoreErrors(remainingSettled);

    if (logging) console.debug(`%c⮂ ✔ All entities restored in ${Date.now() - start}ms`, 'color: green');

    // Mark restore as complete BEFORE processQueue/polling — both of those wait for RESTORED,
    // so dispatching it here breaks the circular dependency.
    store.dispatch(Actions.updateLoading(false));
    store.dispatch(Actions.setRestoreStatus(Actions.RESTORED));

    // Now flush the queue and start polling
    try {
        await processQueue();
        await polling.start('restore');
    }
    catch (err) {
        console.error('⮂ restore failed to start processing the queue', err);
    }
}
